import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  EMPHASIS_ANIMATION_CURSOR_DEFAULTS,
  ENTRANCE_ANIMATION_CURSOR_DEFAULTS,
  EXIT_ANIMATION_CURSOR_DEFAULTS,
  EMPHASIS_LOOPS_TIMING_DEFAULTS,
  EXIT_TIMING_DEFAULTS
} from 'js/config/animationOptions';
import {
  BOUNCE_IN_KEY,
  BOUNCE_LOOP_KEY,
  DEFAULT_HAND_CURSOR_ID,
  DISAPPEAR_KEY,
  DRAG_IN_KEY,
  DRAW_KEY,
  EMPHASIS_STAGE_KEY,
  ENTRANCE_STAGE_KEY,
  EXIT_ANIMATION_ERASE_KEY,
  EXIT_STAGE_KEY,
  FADE_OUT_KEY,
  HAND_DRAW_KEY,
  MOVE_IN_KEY,
  MOVE_OUT_KEY,
  NO_ANIMATION_KEY,
  PAUSE_STAGE_KEY,
  PEN_DRAW_KEY,
  PULSE_LOOP_KEY,
  SHAKE_LOOP_KEY,
  SLINGSHOT_KEY,
  SPIN_LOOP_KEY
} from 'js/config/consts';
import {
  EMPHASIS_DEFAULT_DIRECTION_CONFIG,
  ENTRANCE_DEFAULT_DIRECTION_CONFIG,
  EXIT_DEFAULT_DIRECTION_CONFIG
} from 'js/config/tweens';
import { pauseTime } from 'js/shared/resources/scribedefaults';
import { AllAnimationTypeKeys, ScribeCursorId } from 'js/types';

export type StageKey =
  | typeof ENTRANCE_STAGE_KEY
  | typeof EMPHASIS_STAGE_KEY
  | typeof EXIT_STAGE_KEY
  | typeof PAUSE_STAGE_KEY;
export type ElementConfigurationKey = 'Image' | 'Text' | 'Shape' | 'All';

export interface AnimationConfiguration {
  animationType: AllAnimationTypeKeys;
  duration: number;
  configuration: {
    cursorId: ScribeCursorId | undefined;
    [key: string]: unknown;
  };
}

// Define a specific type for pause settings that only needs duration
export interface PauseConfiguration {
  duration: number;
}

export type AnimationStageSetting = {
  [K in Exclude<StageKey, typeof PAUSE_STAGE_KEY>]: {
    applyTo: 'all' | 'individual';
    elementTypeSettings: {
      [E in ElementConfigurationKey]: AnimationConfiguration;
    };
  };
} & {
  [PAUSE_STAGE_KEY]: {
    applyTo: 'all' | 'individual';
    elementTypeSettings: {
      [E in ElementConfigurationKey]: PauseConfiguration;
    };
  };
};

export type AnimationStageSettingState = {
  lastSynced: number | null;
  stageSettings: AnimationStageSetting;
};

export const DEFAULT_ENTRANCE_CONFIG: AnimationConfiguration = {
  animationType: HAND_DRAW_KEY,
  duration: 2,
  configuration: {
    cursorId: DEFAULT_HAND_CURSOR_ID
  }
};

const DEFAULT_EMPHASIS_CONFIG: AnimationConfiguration = {
  animationType: NO_ANIMATION_KEY,
  duration: 0,
  configuration: {
    cursorId: undefined
  }
};

const DEFAULT_EXIT_CONFIG: AnimationConfiguration = {
  animationType: NO_ANIMATION_KEY,
  duration: 0,
  configuration: {
    cursorId: undefined
  }
};

const DEFAULT_PAUSE_CONFIG: PauseConfiguration = {
  duration: pauseTime
};

export const initialState: AnimationStageSettingState = {
  lastSynced: null,
  stageSettings: {
    [ENTRANCE_STAGE_KEY]: {
      applyTo: 'individual',
      elementTypeSettings: {
        Image: {
          ...DEFAULT_ENTRANCE_CONFIG
        },
        Text: {
          ...DEFAULT_ENTRANCE_CONFIG
        },
        Shape: {
          ...DEFAULT_ENTRANCE_CONFIG,
          duration: 1
        },
        All: {
          ...DEFAULT_ENTRANCE_CONFIG
        }
      }
    },
    [EMPHASIS_STAGE_KEY]: {
      applyTo: 'individual',
      elementTypeSettings: {
        Image: {
          ...DEFAULT_EMPHASIS_CONFIG
        },
        Text: {
          ...DEFAULT_EMPHASIS_CONFIG
        },
        Shape: {
          ...DEFAULT_EMPHASIS_CONFIG
        },
        All: {
          ...DEFAULT_EMPHASIS_CONFIG
        }
      }
    },
    [EXIT_STAGE_KEY]: {
      applyTo: 'individual',
      elementTypeSettings: {
        Image: {
          ...DEFAULT_EXIT_CONFIG
        },
        Text: {
          ...DEFAULT_EXIT_CONFIG
        },
        Shape: {
          ...DEFAULT_EXIT_CONFIG
        },
        All: {
          ...DEFAULT_EXIT_CONFIG
        }
      }
    },
    [PAUSE_STAGE_KEY]: {
      applyTo: 'individual',
      elementTypeSettings: {
        Image: {
          ...DEFAULT_PAUSE_CONFIG
        },
        Text: {
          ...DEFAULT_PAUSE_CONFIG
        },
        Shape: {
          ...DEFAULT_PAUSE_CONFIG
        },
        All: {
          ...DEFAULT_PAUSE_CONFIG
        }
      }
    }
  }
};

const userSettingsSlice = createSlice({
  name: 'userAnimationSettings',
  initialState,
  reducers: {
    setLastSynced(state, action: PayloadAction<number>) {
      state.lastSynced = action.payload;
    },
    loadSettingsSuccess(state, action: PayloadAction<AnimationStageSetting>) {
      state.stageSettings = action.payload;
    },
    setSettingAppliedTo(state, action: PayloadAction<{ stageKey: StageKey; applyTo: 'all' | 'individual' }>) {
      state.stageSettings[action.payload.stageKey].applyTo = action.payload.applyTo;
    },
    setAnimationType(
      state,
      action: PayloadAction<{
        stageKey: StageKey;
        elementType: ElementConfigurationKey;
        animationType: AllAnimationTypeKeys;
      }>
    ) {
      const { stageKey, elementType } = action.payload;

      // Skip if it's a PAUSE stage - animation type doesn't apply to pause
      if (stageKey === PAUSE_STAGE_KEY) {
        return;
      }

      // Safe to cast since we know it's not the PAUSE stage
      const settings = state.stageSettings[stageKey].elementTypeSettings[elementType] as AnimationConfiguration;

      if (settings) {
        settings.animationType = action.payload.animationType;

        let defaultDirectionSetting;
        let defaultCursorConfig;
        let defaultSpinSetting;
        let defaultLoopsSetting;
        let defaultDurationSetting;
        // Default Settings
        switch (action.payload.animationType) {
          // Entrance Defaults
          case MOVE_IN_KEY: {
            defaultDirectionSetting = ENTRANCE_DEFAULT_DIRECTION_CONFIG[MOVE_IN_KEY];
            defaultCursorConfig = ENTRANCE_ANIMATION_CURSOR_DEFAULTS[MOVE_IN_KEY];
            break;
          }
          case DRAG_IN_KEY: {
            defaultDirectionSetting = ENTRANCE_DEFAULT_DIRECTION_CONFIG[DRAG_IN_KEY];
            defaultCursorConfig = ENTRANCE_ANIMATION_CURSOR_DEFAULTS[DRAG_IN_KEY];
            break;
          }
          case BOUNCE_IN_KEY: {
            defaultDirectionSetting = ENTRANCE_DEFAULT_DIRECTION_CONFIG[BOUNCE_IN_KEY];
            defaultCursorConfig = ENTRANCE_ANIMATION_CURSOR_DEFAULTS[BOUNCE_IN_KEY];
            break;
          }
          case HAND_DRAW_KEY: {
            defaultCursorConfig = ENTRANCE_ANIMATION_CURSOR_DEFAULTS[HAND_DRAW_KEY];
            break;
          }
          case PEN_DRAW_KEY: {
            defaultCursorConfig = ENTRANCE_ANIMATION_CURSOR_DEFAULTS[PEN_DRAW_KEY];
            break;
          }
          case DRAW_KEY: {
            defaultCursorConfig = ENTRANCE_ANIMATION_CURSOR_DEFAULTS[DRAW_KEY];
            break;
          }
          case NO_ANIMATION_KEY: {
            defaultCursorConfig = EMPHASIS_ANIMATION_CURSOR_DEFAULTS[NO_ANIMATION_KEY];

            if (stageKey === EMPHASIS_STAGE_KEY) {
              defaultLoopsSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[NO_ANIMATION_KEY].loops;
              defaultDurationSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[NO_ANIMATION_KEY].duration;
            }

            break;
          }
          // Emphasis Defaults
          case PULSE_LOOP_KEY: {
            defaultCursorConfig = EMPHASIS_ANIMATION_CURSOR_DEFAULTS[PULSE_LOOP_KEY];
            defaultLoopsSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[PULSE_LOOP_KEY].loops;
            defaultDurationSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[PULSE_LOOP_KEY].duration;
            break;
          }
          case SHAKE_LOOP_KEY: {
            defaultCursorConfig = EMPHASIS_ANIMATION_CURSOR_DEFAULTS[SHAKE_LOOP_KEY];
            defaultDirectionSetting = EMPHASIS_DEFAULT_DIRECTION_CONFIG[SHAKE_LOOP_KEY];
            defaultLoopsSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[SHAKE_LOOP_KEY].loops;
            defaultDurationSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[SHAKE_LOOP_KEY].duration;
            break;
          }
          case SPIN_LOOP_KEY: {
            defaultCursorConfig = EMPHASIS_ANIMATION_CURSOR_DEFAULTS[SPIN_LOOP_KEY];
            defaultSpinSetting = 'clockwise';
            defaultLoopsSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[SPIN_LOOP_KEY].loops;
            defaultDurationSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[SPIN_LOOP_KEY].duration;
            break;
          }
          case BOUNCE_LOOP_KEY: {
            defaultCursorConfig = EMPHASIS_ANIMATION_CURSOR_DEFAULTS[BOUNCE_LOOP_KEY];
            defaultLoopsSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[BOUNCE_LOOP_KEY].loops;
            defaultDurationSetting = EMPHASIS_LOOPS_TIMING_DEFAULTS[BOUNCE_LOOP_KEY].duration;
            break;
          }

          // Exit Defaults
          case SLINGSHOT_KEY: {
            defaultDirectionSetting = EXIT_DEFAULT_DIRECTION_CONFIG[SLINGSHOT_KEY];
            defaultCursorConfig = EXIT_ANIMATION_CURSOR_DEFAULTS[SLINGSHOT_KEY];
            defaultDurationSetting = EXIT_TIMING_DEFAULTS[SLINGSHOT_KEY];
            break;
          }
          case MOVE_OUT_KEY: {
            defaultDirectionSetting = EXIT_DEFAULT_DIRECTION_CONFIG[MOVE_OUT_KEY];
            defaultCursorConfig = EXIT_ANIMATION_CURSOR_DEFAULTS[MOVE_OUT_KEY];
            defaultDurationSetting = EXIT_TIMING_DEFAULTS[MOVE_OUT_KEY];
            break;
          }
          case EXIT_ANIMATION_ERASE_KEY: {
            defaultCursorConfig = EXIT_ANIMATION_CURSOR_DEFAULTS[EXIT_ANIMATION_ERASE_KEY];
            defaultDurationSetting = EXIT_TIMING_DEFAULTS[EXIT_ANIMATION_ERASE_KEY];
            break;
          }
          case DISAPPEAR_KEY: {
            defaultCursorConfig = EXIT_ANIMATION_CURSOR_DEFAULTS[DISAPPEAR_KEY];
            defaultDurationSetting = EXIT_TIMING_DEFAULTS[DISAPPEAR_KEY];
            break;
          }
          case FADE_OUT_KEY: {
            defaultCursorConfig = EXIT_ANIMATION_CURSOR_DEFAULTS[FADE_OUT_KEY];
            defaultDurationSetting = EXIT_TIMING_DEFAULTS[FADE_OUT_KEY];
            break;
          }

          default:
            break;
        }

        if (defaultDirectionSetting && !settings.configuration.direction) {
          settings.configuration.direction = defaultDirectionSetting;
        }
        if (defaultCursorConfig) {
          settings.configuration.cursorId = defaultCursorConfig.cursorId;
        }
        if (defaultSpinSetting) {
          settings.configuration.spinDirection = defaultSpinSetting;
        }
        if (defaultLoopsSetting) {
          settings.configuration.loops = defaultLoopsSetting;
        }
        if (typeof defaultDurationSetting === 'number') {
          settings.duration = defaultDurationSetting;
        }
      }
    },
    setAnimationConfiguration(
      state,
      action: PayloadAction<{
        stageKey: StageKey;
        elementType: ElementConfigurationKey;
        configuration: { [key: string]: unknown };
      }>
    ) {
      const { stageKey, elementType, configuration } = action.payload;

      // Skip if it's a PAUSE stage - configuration doesn't apply to pause
      if (stageKey === PAUSE_STAGE_KEY) {
        return;
      }

      // Safe to cast since we know it's not the PAUSE stage
      const settings = state.stageSettings[stageKey].elementTypeSettings[elementType] as AnimationConfiguration;

      if (settings) {
        settings.configuration = {
          ...settings.configuration,
          ...configuration
        };
      }
    },
    setAnimationDuration(
      state,
      action: PayloadAction<{ stageKey: StageKey; elementType: ElementConfigurationKey; duration: number }>
    ) {
      const { stageKey, elementType, duration } = action.payload;

      if (state.stageSettings[stageKey].elementTypeSettings[elementType]) {
        state.stageSettings[stageKey].elementTypeSettings[elementType].duration = duration;
      }
    }
  }
});

export const trackAnimationSettingsChanged = createAction('TRACK_USER_ANIMATION_SETTINGS_CHANGED');

export const {
  setSettingAppliedTo,
  setAnimationType,
  setAnimationConfiguration,
  setAnimationDuration,
  setLastSynced,
  loadSettingsSuccess
} = userSettingsSlice.actions;
export default userSettingsSlice.reducer;
